import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { MetricDefinition } from '@metron/shared';
import { useApiClient } from '../hooks/useApiClient.ts';
import { useAdminRefresh } from '../hooks/useAdminRefresh.ts';
import { RefreshTable } from '../components/admin-refresh/RefreshTable.tsx';
import { RefreshDrawer } from '../components/admin-refresh/RefreshDrawer.tsx';
import {
  expandMetricDefinitions,
  type MetricDefinitionSummary,
  type RefreshableMetric,
  STATIC_REFRESHABLES,
} from '../components/admin-refresh/registry.ts';

export function AdminRefreshPage() {
  const apiClient = useApiClient();
  const definitions = useQuery({
    queryKey: ['admin-refresh', 'metric-definitions'],
    queryFn: async () => {
      const response = await apiClient.get<{ metrics: MetricDefinition[] }>(
        'metrics/definitions',
      );
      return response.metrics
        .filter((m) => m.enabled)
        .map<MetricDefinitionSummary>((m) => ({
          id: m.id,
          displayName: m.displayName,
          backfillDepth: m.backfillDepth,
          schedule: m.schedule,
        }));
    },
  });

  const rows: RefreshableMetric[] = useMemo(() => {
    const dynamic = definitions.data ? expandMetricDefinitions(definitions.data) : [];
    return [...STATIC_REFRESHABLES, ...dynamic];
  }, [definitions.data]);

  const [drawerMetric, setDrawerMetric] = useState<RefreshableMetric | null>(null);
  const [inflightIds, setInflightIds] = useState<Set<string>>(() => new Set());
  const refresh = useAdminRefresh();

  function runRefresh(metric: RefreshableMetric, params: Record<string, string>) {
    toast.info(`Refresh started for ${metric.displayName}…`);
    setInflightIds((prev) => new Set(prev).add(metric.id));
    refresh.mutate(
      { metric, params },
      {
        onSettled: (result) => {
          setInflightIds((prev) => {
            const next = new Set(prev);
            next.delete(metric.id);
            return next;
          });
          if (!result) {
            toast.error(`Refresh failed for ${metric.displayName}.`);
            return;
          }
          switch (result.kind) {
            case 'ok':
              toast.success(`Refresh completed for ${metric.displayName}.`);
              break;
            case 'may_still_be_running':
              toast.info(
                `Refresh for ${metric.displayName} may still be running — check back in a few minutes.`,
              );
              break;
            case 'forbidden':
              toast.error('You no longer have permission. Please sign in again.');
              break;
            case 'not_found':
              toast.error(`${metric.displayName} not found. Reload the page.`);
              break;
            case 'validation_error':
              toast.error(result.message);
              break;
            case 'generic_failure':
              toast.error(`Refresh failed for ${metric.displayName}: ${result.message}`);
              break;
          }
        },
      },
    );
  }

  function handleRefreshClick(metric: RefreshableMetric) {
    if (metric.params.kind === 'none') {
      runRefresh(metric, {});
    } else {
      setDrawerMetric(metric);
    }
  }

  return (
    <div className='container mx-auto py-6 px-4'>
      <div className='mb-4'>
        <h1 className='text-2xl font-bold'>Refresh metrics</h1>
        <p className='text-muted-foreground text-sm'>
          Trigger manual refreshes of Wellspring's computed metrics. Most runs continue in the
          background past the request timeout — wait a few minutes after a toast before re-checking
          results.
        </p>
      </div>
      <RefreshTable
        metrics={rows}
        onRefresh={handleRefreshClick}
        isPendingFor={(id) => inflightIds.has(id)}
      />
      {drawerMetric && (
        <RefreshDrawer
          metric={drawerMetric}
          open
          onClose={() => setDrawerMetric(null)}
          onSubmit={(params) => {
            const m = drawerMetric;
            setDrawerMetric(null);
            runRefresh(m, params);
          }}
        />
      )}
    </div>
  );
}
